import { useRef, useState } from "react";
import { monthLabel } from "@/components/record/recordFormat";

/**
 * A month that reads "Mar 2027", or "Add" when unset; click to pick. It saves
 * once, when the box loses focus or on Enter, as the first of the month (null
 * when cleared). Escape cancels. The old month inputs saved on every change,
 * which can send a write per keystroke while a year is typed.
 */
export function MonthField({ label, value, onSave }: {
  label: string; value: string | null | undefined; onSave: (ymd: string | null) => void;
}) {
  const month = value ? String(value).slice(0, 7) : "";
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const cancelled = useRef(false);

  const commit = () => {
    setEditing(false);
    if (cancelled.current) { cancelled.current = false; return; }
    if (draft !== month) onSave(draft ? `${draft}-01` : null);
  };

  if (editing) {
    return (
      <input
        type="month"
        autoFocus
        aria-label={label}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") { cancelled.current = true; e.currentTarget.blur(); }
        }}
        className="rounded border bg-background px-1 py-0.5 text-[13px]"
      />
    );
  }
  return (
    <button
      type="button"
      onClick={() => { setDraft(month); setEditing(true); }}
      aria-label={`Edit ${label}: ${month ? monthLabel(month) : "empty"}`}
      className={month ? "hover:underline" : "font-semibold text-primary-text hover:underline"}
    >
      {month ? monthLabel(month) : "Add"}
    </button>
  );
}
